import { AxiosRequestConfig } from "axios";

import { moneefloApi } from "./client";

/**
 * Drop-in replacement for Xano's refund call, so the refund wizard keeps its
 * three steps and only its data source changes.
 *
 * Xano took the whole recalculation — rate, margins, INR amount — and worked
 * out the reversal itself. The new endpoint takes the amount to give back and
 * posts the mirror of the original payment, capped at what was received. The
 * rate and margin fields the wizard collects are carried into the reason so
 * they survive on the order's history rather than being dropped.
 */

type Payload<T> = AxiosRequestConfig<T>;

export type postRefundOrderRequestType = Partial<{
  headers: { Authorization: string };
  order_id: string;
  customer_rate: string;
  reason: string;
  ad_ref_no: string;
  fx_amount: string;
  inr_amount: string;
  settlement_rate: string;
  my_margin: string;
  partner_margin: string;
  document: { doc_list_id: string; file: string }[];
}>;

export async function postRefundOrder(
  payload: Payload<postRefundOrderRequestType>,
): Promise<{ status: number; data: Record<string, unknown> }> {
  const body = payload.data ?? {};
  if (!body.order_id) throw new Error("An order is required to refund.");

  const inr = Number(body.inr_amount);
  if (!Number.isFinite(inr) || inr <= 0) {
    throw new Error("A refund amount in rupees is required.");
  }

  if (body.document?.length) {
    // Documents would be silently dropped otherwise, and a refund advice that
    // was never stored is worse than one the operator knows to re-attach.
    throw new Error(
      "Refund documents cannot be attached yet — object storage has no public address. Record the refund, then attach the advice from the order screen once storage is live.",
    );
  }

  const detail = [
    body.reason?.trim(),
    body.ad_ref_no ? `AD ref ${body.ad_ref_no}` : null,
    body.customer_rate ? `rate ${body.customer_rate}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const { data } = await moneefloApi.post(`/orders/${body.order_id}/refunds`, {
    amountMinor: Math.round(inr * 100),
    reason: detail || "Refund",
    reference: body.ad_ref_no || undefined,
    refundRate: body.customer_rate ? Number(body.customer_rate) : undefined,
  });

  const refund = data as { id?: string; amountMinor?: string };
  // The status matters: the wizard only finishes on `response.status === 200`,
  // and unwrapping it away left the refund created on the server with the
  // spinner still turning. An operator who reloads at that point refunds the
  // same payment twice.
  return {
    status: 200,
    data: {
      ...refund,
      // The screen renders `amount_inr`; the API speaks in paise.
      amount_inr: (Number(refund.amountMinor ?? 0) / 100).toFixed(2),
    },
  };
}
